<template>
    <div>
        <div class="modal" id="create_shift_modal" tabindex="-1" aria-labelledby="create_shift_modal" aria-hidden="true">
            <div class="modal-dialog" :class="{ 'modal-xl': recurring.enabled }">
                <div class="modal-content shadow-3">
                    <div class="modal-header flex-wrap">
                        <h5 class="modal-title">
                            {{ $trans.get('__JSON__.Create new Shift') }}
                        </h5>
                        <div class="form-check form-switch">
                            <label for="recurring">{{ $trans.get('__JSON__.Recurring') }}</label>
                            <input class="form-check-input" type="checkbox" id="recurring" name="recurring" v-model="recurring.enabled">
                        </div>
                    </div>
                    <div class="modal-body">
                        <fieldset :disabled="submitting">
                            <div class="row">
                                <div :class="{ 'col-6': recurring.enabled, 'col-12': ! recurring.enabled }">
                                    <div class="row">
                                        <div class="col-12 mb-5" v-show="Object.keys(locations).length > 1">
                                            <label class="form-label" for="location">
                                                {{ $trans.get('__JSON__.Choose a Location') }}
                                            </label>
                                            <vue-multiselect
                                                :options="locations"
                                                :multiple="false"
                                                :close-on-select="true"
                                                :preserve-search="false"
                                                :disabled="submitting"
                                                :placeholder="$trans.get('__JSON__.Make your selection')"
                                                label="label"
                                                track-by="key"
                                                v-model="shift.location"
                                                class="form-select form-select-sm"
                                                :class="{ 'is-invalid': errors['location'] }"
                                                id="location"
                                            />
                                            <span class="invalid-feedback" role="alert" v-if="errors['location']">
                                                <strong>{{ errors['location'][0] }}</strong>
                                            </span>
                                        </div>

                                        <div class="col-12 mb-5">
                                            <label class="form-label" for="start_date">
                                                {{ $trans.get('__JSON__.Start Date and Time') }}
                                            </label>
                                            <div class="input-group">
                                                <flat-pickr id="start_date" class="form-control" :class="{ 'is-invalid': errors['start_date'] }" :config="{...flatPickr, minDate: getToday}" v-model="shift.startDate" />
                                                <span class="invalid-feedback" role="alert" v-if="errors['start_date']">
                                                    <strong>{{ errors['start_date'][0] }}</strong>
                                                </span>
                                                <input type="time" class="form-control" :class="{ 'is-invalid': errors['start_time'] }" v-model="shift.startTime">
                                                <span class="invalid-feedback" role="alert" v-if="errors['start_time']">
                                                    <strong>{{ errors['start_time'][0] }}</strong>
                                                </span>
                                            </div>
                                        </div>

                                        <div class="col-12 mb-5">
                                            <label class="form-label" for="end_date">
                                                {{ $trans.get('__JSON__.End Date and Time') }}
                                            </label>
                                            <div class="input-group">
                                                <flat-pickr id="end_date" class="form-control" :class="{ 'is-invalid': errors['end_date'] }" :config="{...flatPickr, minDate: this.shift.startDate, maxDate: this.addDays(this.shift.startDate, 1)}" v-model="shift.endDate" />
                                                <span class="invalid-feedback" role="alert" v-if="errors['end_date']">
                                                    <strong>{{ errors['end_date'][0] }}</strong>
                                                </span>
                                                <input type="time" class="form-control" :class="{ 'is-invalid': errors['end_time'] }" v-model="shift.endTime">
                                                <span class="invalid-feedback" role="alert" v-if="errors['end_time']">
                                                    <strong>{{ errors['end_time'][0] }}</strong>
                                                </span>
                                            </div>
                                        </div>

                                        <div class="col-12 mb-5">
                                            <label class="form-label" for="job">
                                                {{ $trans.get('__JSON__.Job and Slots Available') }}
                                            </label>
                                            <div class="input-group">
                                                <input type="text" id="job" class="form-control" :class="{ 'is-invalid': errors['job'] }" :placeholder="$trans.get('__JSON__.Job Title')" v-model="shift.job">
                                                <span class="invalid-feedback" role="alert" v-if="errors['job']">
                                                    <strong>{{ errors['job'][0] }}</strong>
                                                </span>
                                                <input type="number" class="form-control" :class="{ 'is-invalid': errors['slots'] }" :placeholder="25" min="1" v-model="shift.slots">
                                                <span class="invalid-feedback" role="alert" v-if="errors['slots']">
                                                    <strong>{{ errors['slots'][0] }}</strong>
                                                </span>
                                            </div>
                                        </div>

                                        <div class="col-12">
                                            <label class="form-label" for="note">
                                                {{ $trans.get('__JSON__.Notes') }} ({{ $trans.get('__JSON__.optional') }})
                                            </label>
                                            <textarea id="note" cols="30" rows="10" class="form-control" v-model="shift.notes" :class="{ 'is-invalid': errors['notes'] }"></textarea>
                                            <span class="invalid-feedback" role="alert" v-if="errors['notes']">
                                                <strong>{{ errors['notes'][0] }}</strong>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="row" v-if="recurring.enabled">
                                        <div class="col-12">
                                            <label class="form-label" for="recurring_interval">
                                                {{ $trans.get('__JSON__.Apply recurring') }}
                                            </label>
                                            <div class="input-group">
                                                <span class="input-group-text">{{ $trans.get('__JSON__.Repeat each') }}</span>
                                                <input type="number" id="recurring_interval" class="form-control" :class="{ 'is-invalid': errors['recurring_interval'] }" min="1" :placeholder="$trans.get('__JSON__.Interval')" v-model="recurring.interval.amount">
                                                <span class="invalid-feedback" role="alert" v-if="errors['recurring_interval']">
                                                    <strong>{{ errors['recurring_interval'][0] }}</strong>
                                                </span>
                                                <select class="form-control" :class="{ 'is-invalid': errors['recurring_interval'] }" v-model="recurring.interval.type">
                                                    <option value="D">{{ $trans.choice('__JSON__.Days', recurring.interval.amount) }}</option>
                                                    <option value="W">{{ $trans.choice('__JSON__.Weeks', recurring.interval.amount) }}</option>
                                                </select>
                                                <span class="invalid-feedback" role="alert" v-if="errors['recurring_interval']">
                                                    <strong>{{ errors['recurring_interval'][0] }}</strong>
                                                </span>
                                            </div>
                                        </div>

                                        <div class="col-12 mt-5" v-if="recurring.interval.type === 'W'">
                                            <label class="form-label" for="recurring_weekdays">
                                                {{ $trans.get('__JSON__.Repeat on') }}
                                            </label>
                                            <div class="input-group no-shadow" :class="{ 'is-invalid': errors['recurring_weekdays'] }">
                                                <div class="form-check form-check-weekday" v-for="weekday in recurring.weekdays">
                                                    <input class="form-check-input" type="checkbox" :id="weekday.label.toLowerCase()" v-model="weekday.selected" @change="checkSelectedWeekdays">
                                                    <label class="form-check-label" :for="weekday.label.toLowerCase()">
                                                        <span>{{ shortWeekday(weekday) }}</span>
                                                    </label>
                                                </div>
                                                <span class="invalid-feedback" role="alert" v-if="errors['recurring_weekdays']">
                                                    <strong>{{ errors['recurring_weekdays'][0] }}</strong>
                                                </span>
                                            </div>
                                        </div>

                                        <div class="col-12 mt-5">
                                            <label class="form-label" for="recurring_ends">
                                                {{ $trans.get('__JSON__.Ends on') }}
                                            </label>
                                            <flat-pickr id="recurring_ends" class="form-control" :class="{ 'is-invalid': errors['recurring_ends'] }" :config="{...flatPickr, minDate: addDays(shift.endDate, 1), maxDate: addMonths(shift.startDate, 3) }" v-model="recurring.endDate" />
                                            <span class="d-block mt-2 text-sm text-muted">
                                                {{ $trans.get('__JSON__.Up to a maximum of 3 months from the start date (:max)', { max: addMonths(shift.startDate, 3, DATE_YMD) }) }}
                                            </span>
                                            <span class="invalid-feedback" role="alert" v-if="errors['recurring_ends']">
                                                <strong>{{ errors['recurring_ends'][0] }}</strong>
                                            </span>
                                        </div>

                                        <div class="col-12 mt-5">
                                            <div>
                                                <label class="form-label" for="recurring_differences">
                                                    {{ $trans.get('__JSON__.Shift differences') }} ({{ $trans.get('__JSON__.optional') }})
                                                    <a tabindex="0" class="ml-2 text-black" role="button" data-bs-toggle="popover" data-bs-trigger="focus" :title="$trans.get('__JSON__.Explanation: shift differences')" :data-bs-content="$trans.get('__JSON__.With this option, you\'ll have the ability to add different data within the given range. For example, you know there is a public holiday for which you\'ll need to exclude or have a different amount of slots and provide an additional note. Date and slots are required.')">
                                                        <i class="bi bi-question-circle"></i>
                                                    </a>
                                                </label>
                                                <button class="btn btn-sm btn-primary float-end" @click="addRecurringDifference">
                                                    <i class="bi bi-calendar-plus"></i>
                                                </button>
                                            </div>
                                            <div class="mt-5">
                                                <div class="input-group mb-5" :class="{ 'is-invalid': (errors[`recurring_differences.${index}.date`] || errors[`recurring_differences.${index}.slots`] || errors[`recurring_differences.${index}.notes`]) || difference.slots === 0 }" v-for="(difference, index) in recurring.differences">
                                                    <flat-pickr :id="`diff-${index}`" :placeholder="$trans.get('__JSON__.Diff date')" class="form-control" :config="{...diffPickrConfig}" :class="{ 'is-invalid': errors[`recurring_differences.${index}.date`] }" v-model="difference.date" />
                                                    <span class="invalid-feedback" role="alert" v-if="errors[`recurring_differences.${index}.date`]">
                                                        <strong>{{ errors[`recurring_differences.${index}.date`][0] }}</strong>
                                                    </span>
                                                    <input type="number" class="form-control" :class="{ 'is-invalid': errors[`recurring_differences.${index}.slots`] }" :placeholder="1" :min="1" v-model="difference.slots" v-show="difference.slots !== 0">
                                                    <span class="invalid-feedback" role="alert" v-if="errors[`recurring_differences.${index}.slots`]" v-show="difference.slots !== 0">
                                                        <strong>{{ errors[`recurring_differences.${index}.slots`][0] }}</strong>
                                                    </span>
                                                    <textarea class="form-control" rows="1" :class="{ 'is-invalid': errors[`recurring_differences.${index}.notes`] }" :placeholder="$trans.get('__JSON__.Addition')" v-model="difference.notes"  v-show="difference.slots !== 0"></textarea>
                                                    <span class="invalid-feedback" role="alert" v-if="errors[`recurring_differences.${index}.notes`]" v-show="difference.slots !== 0">
                                                        <strong>{{ errors[`recurring_differences.${index}.notes`][0] }}</strong>
                                                    </span>
                                                    <span class="input-group-text" v-show="difference.slots === 0">
                                                        {{ $trans.get('__JSON__.We will not include this date') }}
                                                    </span>
                                                    <div class="input-group-text ms-auto">
                                                        <div class="dropdown">
                                                            <a class="text-muted" href="#" role="button" data-bs-toggle="dropdown">
                                                                <i class="bi bi-three-dots-vertical"></i>
                                                            </a>
                                                            <div class="dropdown-menu">
                                                                <a href="#!" class="dropdown-item" @click="markRecurring(difference, 'reset')" v-if="difference.slots === 0">
                                                                    {{ $trans.get('__JSON__.Reset') }}
                                                                </a>
                                                                <a href="#!" class="dropdown-item" @click="markRecurring(difference, 'excluded')" v-else>
                                                                    {{ $trans.get('__JSON__.Exclude') }}
                                                                </a>
                                                                <a href="#!" class="dropdown-item text-danger" @click="removeRecurringDifference(index)">
                                                                    {{ $trans.get('__JSON__.Remove') }}
                                                                </a>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-sm btn-neutral" data-bs-dismiss="modal">
                            {{ $trans.get('__JSON__.Close') }}
                        </button>
                        <button type="button" class="btn btn-sm btn-primary" @click="submit" :disabled="submitting || calculating">
                            <div class="spinner-border spinner-border-sm" role="status" v-if="submitting || calculating">
                                <span class="visually-hidden">{{ $trans.get('__JSON__.Loading...') }}</span>
                            </div>
                            <span v-else-if="recurring.enabled">{{ $trans.get('__JSON__.Confirm') }}</span>
                            <span v-else>{{ $trans.get('__JSON__.Save') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <RecurringShiftConfirmation :confirmationData="confirmationData" />
    </div>
</template>

<script>
import ModalHelper from '../mixins/ModalHelper'
import DateHelpers from '../mixins/DateHelpers'
import RequestHandler from '../mixins/RequestHandler'
import RecurringShiftConfirmation from '../shared/RecurringShiftConfirmationModal'
import PopoverHelper from '../mixins/PopoverHelper'
import DatePicker from '../mixins/DatePicker'
import {DateTime} from 'luxon'

const currentWeekday = DateTime.now().weekday

export default {
    name: 'AddNewShiftComponent',
    computed: {
        DateTime() {
            return DateTime
        },
        recurringInterval() {
            const interval = this.recurring.interval
            return `P${interval.amount}${interval.type}`
        },
        diffPickrConfig() {
            const intervalType = this.recurring.interval.type
            // const selectedDates = this.recurring.differences.map(it => it.date).filter(it => it)
            const selectedWeekdays = this.recurring.weekdays.filter(it => it.selected).map(it => it.day)

            return {
                ...this.flatPickr,
                minDate: this.shift.startDate,
                maxDate: this.recurring.endDate || this.addMonths(this.shift.startDate, 3),
                "disable": [
                    function (date) {
                        if (intervalType === 'W') return ! selectedWeekdays.includes(date.getDay())
                        return false

                        // FIXME: the below is giving issues when disabling already selected dates; then the system is not allowing it to show
                        // let ymd = DateTime.fromJSDate(date).toFormat('yyyy-MM-dd')
                        //
                        // if (intervalType !== 'W') return selectedDates.includes(ymd)
                        //
                        // return selectedDates.includes(ymd)
                        //     || ! selectedWeekdays.includes(date.getDay())
                    }
                ],
            }
        },
    },
    mixins: [ModalHelper, DateHelpers, RequestHandler, PopoverHelper, DatePicker],
    components: {RecurringShiftConfirmation},
    props: {
        today: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            submitting: false,
            calculating: false,
            locations: [],
            shift: {
                location: null,
                startDate: this.today,
                startTime: null,
                endDate: this.today,
                endTime: null,
                job: null,
                slots: null,
                notes: null,
            },
            recurring: {
                enabled: false,
                confirmed: false,
                interval: {
                    amount: 1,
                    type: 'W',
                },
                endDate: null,
                weekdays: [
                    { day: 1, selected: currentWeekday === 1, label: 'Monday' },
                    { day: 2, selected: currentWeekday === 2, label: 'Tuesday' },
                    { day: 3, selected: currentWeekday === 3, label: 'Wednesday' },
                    { day: 4, selected: currentWeekday === 4, label: 'Thursday' },
                    { day: 5, selected: currentWeekday === 5, label: 'Friday' },
                    { day: 6, selected: currentWeekday === 6, label: 'Saturday' },
                    { day: 0, selected: currentWeekday === 0, label: 'Sunday' },
                ],
                differences: [],
            },
            flatPickr: {
                locale: document.documentElement.lang.slice(0, 2),
                wrap: true,
                weekNumbers: true,
                altInput: true,
                altFormat: 'j F Y',
                altInputClass: 'form-select',
                dateFormat: 'Y-m-d',
            },
            recurringData: {},
            confirmationData: {},
        }
    },
    watch: {
        locations() {
            this.firstLocation()
        },
        'shift.startDate'(newDate, oldDate) {
            if (new Date(this.shift.endDate) < new Date(this.shift.startDate)) {
                this.shift.endDate = this.shift.startDate
            }

            // update weekday selector
            this.resetWeekdays()
            if (! this.recurring.weekdays[this.weekdayNumber(newDate) - 1].selected) this.keySelectWeekday(this.weekdayNumber(newDate))
        },
        'recurring.enabled'() {
            this.popovers()
            if (this.recurring.enabled) this.keySelectWeekday(this.weekdayNumber(this.shift.startDate))
        },
    },
    methods: {
        firstLocation() {
            if (this.locations.length === 1) this.shift.location = this.locations[0]
        },
        reset() {
            this.errors = {}
            this.submitting = false
            this.calculating = false
            this.shift = {
                location: null,
                startDate: this.today,
                startTime: null,
                endDate: this.today,
                endTime: null,
                job: null,
                slots: null,
                notes: null,
            }
            this.recurring = {
                enabled: false,
                confirmed: false,
                interval: {
                    amount: 1,
                    type: 'W',
                },
                endDate: null,
                weekdays: [
                    { day: 1, selected: currentWeekday === 1, label: 'Monday' },
                    { day: 2, selected: currentWeekday === 2, label: 'Tuesday' },
                    { day: 3, selected: currentWeekday === 3, label: 'Wednesday' },
                    { day: 4, selected: currentWeekday === 4, label: 'Thursday' },
                    { day: 5, selected: currentWeekday === 5, label: 'Friday' },
                    { day: 6, selected: currentWeekday === 6, label: 'Saturday' },
                    { day: 0, selected: currentWeekday === 0, label: 'Sunday' },
                ],
                differences: [],
            }
            this.recurringData = {}
            this.confirmationData = {}
        },
        resetWeekdays() {
            this.recurring.weekdays = this.recurring.weekdays.map(item => {
                return {...item, selected: false}
            })
        },
        addRecurringDifference() {
            this.recurring.differences.push({
                date: null,
                slots: null,
                notes: null,
            })
        },
        markRecurring(item, type) {
            if (type === 'reset') item.slots = 1
            else if (type === 'excluded') item.slots = 0
        },
        removeRecurringDifference(index) {
            this.recurring.differences.splice(index, 1)
        },
        keySelectWeekday(pressed) {
            if (pressed >=1 && pressed <= 7) {
                --pressed

                if (this.recurring.weekdays[pressed].selected && this.weekdayNumber(this.shift.startDate) -1 === pressed) return // cannot disable if its the start date
                this.recurring.weekdays[pressed].selected = ! this.recurring.weekdays[pressed].selected
            }
        },
        async calculateAndConfirm() {
            this.calculating = true

            this.recurringData = {
                recurring_interval: this.recurringInterval,
                recurring_ends: this.recurring.endDate,
                recurring_weekdays: this.recurring.weekdays.filter(item => item.selected).map(item => item.day),
                recurring_differences: this.recurring.differences,
            }

            this.makeRequest('post', '/api/shifts/recurring/calculate', {
                location: (this.shift.location) ? this.shift.location.key : null,
                start_date: this.shift.startDate,
                start_time: this.shift.startTime,
                end_date: this.shift.endDate,
                end_time: this.shift.endTime,
                job: this.shift.job,
                slots: this.shift.slots,
                notes: this.shift.notes,
                ...this.recurringData
            }, async response => {
                this.confirmationData = response.data.data
                const elementId = 'recurring_shift_confirm_modal'
                await this.openModal(elementId, {backdrop: 'static', keyboard: false})
                // TODO: fix closing listeners, listeners are always open at the moment
                await this.listenModal(elementId, 'hidden.bs.modal', () => {
                    if (this.recurring.confirmed && !this.submitting) this.save()
                })
            }, error => {}, () => {
                this.calculating = false
            })
        },
        async submit() {
            if (this.recurring.enabled) {
                await this.calculateAndConfirm()

                return
            }

            // submit non-recurring shift
            this.save()
        },
        save() {
            this.submitting = true

            const data = {
                ...this.recurringData,
            }

            this.makeRequest('post', '/api/shifts/create', {
                location: (this.shift.location) ? this.shift.location.key : null,
                start_date: this.shift.startDate,
                start_time: this.shift.startTime,
                end_date: this.shift.endDate,
                end_time: this.shift.endTime,
                job: this.shift.job,
                slots: this.shift.slots,
                notes: this.shift.notes,
                ...data
            }, response => {
                this.reset()
                this.closeModal('create_shift_modal')
            }, error => {}, () => {
                this.submitting = false
            })
        },
        shortWeekday(weekday) {
            return this.$trans.get(`__JSON__.${weekday.label}`).substring(0, 2)
        },
        checkSelectedWeekdays() {
            const selectedWeekday = DateTime.fromISO(this.shift.startDate).weekday

            this.recurring.weekdays = this.recurring.weekdays.map(current => {
                if (current.day === selectedWeekday) current.selected = true
                return current
            })
        },
    },
    beforeMount() {
        this.recurring = {
            enabled: false,
            confirmed: false,
            interval: {
                amount: 1,
                type: 'W',
            },
            endDate: null,
            weekdays: [
                { day: 1, selected: currentWeekday === 1, label: 'Monday' },
                { day: 2, selected: currentWeekday === 2, label: 'Tuesday' },
                { day: 3, selected: currentWeekday === 3, label: 'Wednesday' },
                { day: 4, selected: currentWeekday === 4, label: 'Thursday' },
                { day: 5, selected: currentWeekday === 5, label: 'Friday' },
                { day: 6, selected: currentWeekday === 6, label: 'Saturday' },
                { day: 0, selected: currentWeekday === 0, label: 'Sunday' },
            ],
            differences: [],
        }
        this.confirmationData = {}
    },
    async mounted() {
        this.popovers()
        axios.get('/api/locations')
            .then(response => this.locations = response.data)
            .catch(error => console.error(error))

        await this.listenModal('create_shift_modal', 'show.bs.modal', () => this.firstLocation())

        window.addEventListener('keydown', (e) => {
            if (e.target.getAttribute('id') !== 'create_shift_modal') return
            if (! this.recurring.enabled) return
            let pressed = e.key
            this.keySelectWeekday(pressed)
        });
    },
}
</script>
